import { useState } from 'react';
import { ChevronDown, Mail, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';
import type { FillVariable, ResolvedSignatory } from '../utils';

/** How many detail rows are shown before the "See more" toggle. */
const DETAILS_PREVIEW_COUNT = 4;

interface ConfirmDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All leaf variables; only those with a non-empty value are shown. */
  leafVariables: FillVariable[];
  values: Record<string, string>;
  signatories: ResolvedSignatory[];
  onConfirm: () => void;
}

/**
 * Irreversible-generation consent gate for the DOCX fill flow. Shows a summary
 * of the filled details (collapsed to a few rows with a "See more" toggle) and
 * a collapsible signatory list, then confirms generation.
 */
export function ConfirmDetailsDialog({
  open,
  onOpenChange,
  leafVariables,
  values,
  signatories,
  onConfirm,
}: ConfirmDetailsDialogProps) {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [signatoriesExpanded, setSignatoriesExpanded] = useState(false);

  const details = leafVariables.filter((v) => String(values[v.name] ?? '').trim() !== '');
  const visibleDetails = showAllDetails ? details : details.slice(0, DETAILS_PREVIEW_COUNT);
  const hiddenCount = details.length - DETAILS_PREVIEW_COUNT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm your details</DialogTitle>
          <DialogDescription>
            Please confirm that all the information you provided is correct. This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>

        {/* Filled-data summary */}
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-globe-grey">
            Your details
          </p>
          <dl className="divide-y divide-border rounded-xl border border-border bg-[hsl(var(--muted))]/20">
            {visibleDetails.map((v) => (
              <div
                key={v.name}
                className="flex items-start justify-between gap-4 px-3 py-2 text-sm"
              >
                <dt className="min-w-0 shrink-0 max-w-[45%] truncate text-globe-grey">{v.label}</dt>
                <dd className="min-w-0 flex-1 wrap-break-word text-right font-medium text-[hsl(var(--fg))]">
                  {String(values[v.name]).trim()}
                </dd>
              </div>
            ))}
          </dl>

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllDetails((prev) => !prev)}
              className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-selise-blue transition-colors hover:text-selise-blue/80"
            >
              {showAllDetails ? 'See less' : `See more (${hiddenCount})`}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAllDetails ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>

        {/* Collapsible signatory list (default collapsed) */}
        {signatories.length > 0 && (
          <Collapsible open={signatoriesExpanded} onOpenChange={setSignatoriesExpanded}>
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-medium text-[hsl(var(--fg))] transition-colors hover:bg-muted">
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4 text-globe-grey" />
                Signatories
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-globe-grey">
                  {signatories.length}
                </span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-globe-grey transition-transform ${
                  signatoriesExpanded ? 'rotate-180' : ''
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <ul className="space-y-2">
                {[...signatories]
                  .sort((a, b) => (a.signingOrder ?? Infinity) - (b.signingOrder ?? Infinity))
                  .map((s) => (
                    <li key={s.key} className="rounded-xl border border-border bg-card p-3 text-sm">
                      <p className="font-medium text-[hsl(var(--fg))]">{s.name}</p>
                      {(s.role || s.company) && (
                        <p className="text-xs text-globe-grey">
                          {[s.role, s.company].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {s.email && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-globe-grey">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{s.email}</span>
                        </p>
                      )}
                    </li>
                  ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Go back
          </Button>
          <Button onClick={onConfirm} className="gap-2">
            <Sparkles className="h-4 w-4" />
            Confirm &amp; generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDetailsDialog;
